"use client";

import { useSearch } from "@/context/SearchContext";
import { useLocale } from "@/context/LocaleContext";
import { I18nData } from "@/types/i18n";
import { Branding } from "@/types/branding";
import { processHtmlLinks } from "@/utils/process-html-links";
import "./empty-state.scss";

interface EmptyStateProps {
  i18nData?: I18nData;
  branding: Branding | null;
}

export default function EmptyState({ i18nData, branding }: EmptyStateProps) {
  const { searchResult, currentSearchTerm } = useSearch();
  const { locale } = useLocale();

  const openInNewWindow = branding?.open_in_new_window !== false;
  const noResultsFound = searchResult?.data.length === 0;
  const initialSearchingState = !currentSearchTerm;

  if (initialSearchingState) {
    const instructionsHtml = processHtmlLinks(
      i18nData?.[locale]?.instructions,
      openInNewWindow,
    );

    return (
      <div id="startSearch">
        <svg
          width="100px"
          height="100px"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
          className="searchIcon"
          id="startSearchIcon"
          fill="none"
        >
          <path
            d="M11 6C13.7614 6 16 8.23858 16 11M16.6588 16.6549L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <div
          id="searchText"
          dangerouslySetInnerHTML={{ __html: instructionsHtml }}
        />
      </div>
    );
  }

  if (noResultsFound) {
    const noResultsHtml = processHtmlLinks(
      i18nData?.[locale]?.no_results,
      openInNewWindow,
    );

    return (
      <div id="notFoundSearch">
        <svg
          className="searchIcon"
          id="nothingFoundIcon"
          width="100px"
          height="100px"
          viewBox="0 0 32 32"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M30.885 29.115l-10.132-10.132c1.555-1.9 2.497-4.355 2.497-7.029 0-3.092-1.26-5.89-3.294-7.909l-0.001-0.001h-0.002c-2.036-2.040-4.851-3.301-7.961-3.301-6.213 0-11.249 5.036-11.249 11.249 0 3.11 1.262 5.924 3.301 7.961l0 0c2.019 2.036 4.817 3.297 7.91 3.297 2.674 0 5.128-0.942 7.048-2.513l-0.020 0.016 10.132 10.132c0.226 0.226 0.539 0.366 0.884 0.366 0.691 0 1.251-0.56 1.251-1.251 0-0.345-0.14-0.658-0.366-0.884l0 0zM5.813 18.186c-1.583-1.583-2.563-3.771-2.563-6.187 0-4.832 3.917-8.749 8.749-8.749 2.416 0 4.603 0.979 6.187 2.563h0.002c1.583 1.583 2.563 3.77 2.563 6.186s-0.979 4.602-2.561 6.185l0-0-0.004 0.002-0.003 0.004c-1.583 1.582-3.769 2.56-6.183 2.56-2.417 0-4.604-0.98-6.187-2.564l-0-0zM13.768 12l1.944-1.944c0.226-0.226 0.366-0.539 0.366-0.884 0-0.69-0.56-1.25-1.25-1.25-0.345 0-0.658 0.14-0.884 0.366l-1.944 1.944-1.944-1.944c-0.226-0.226-0.539-0.366-0.884-0.366-0.69 0-1.25 0.56-1.25 1.25 0 0.345 0.14 0.658 0.366 0.884v0l1.944 1.944-1.944 1.944c-0.226 0.226-0.366 0.539-0.366 0.884 0 0.69 0.56 1.25 1.25 1.25 0.345 0 0.658-0.14 0.884-0.366v0l1.944-1.944 1.944 1.944c0.226 0.226 0.539 0.366 0.884 0.366 0.69 0 1.25-0.56 1.25-1.25 0-0.345-0.14-0.658-0.366-0.884v0z"></path>
        </svg>
        <div
          id="notFoundText"
          dangerouslySetInnerHTML={{ __html: noResultsHtml }}
        />
      </div>
    );
  }

  return null;
}
